// src/pages/modules/DataEntry/RegisterForm/RegisterForm.tsx
// แสดงทุก page เป็น Card ไล่ลงมา — ง่ายต่อการ re-check ก่อน submit

import React from 'react'
import { Alert, Badge, Button, Card, Form, Row, Spinner, Table } from 'react-bootstrap'
import { AuthContext, dbRead, dbSet } from '../../../../_services/firebase'
import { DB_PHOTO_ITEM } from '../../../../_config/db_paths'
import { saveNewCustomer, PATH } from '../../../../_services/customer.service'
import { buildInitialValues, validatePage, type FormDoc, type Field } from '../../../../_services/formEngine'
import { genCustomerId, toastDanger, toastSuccess } from '../../../../_utils'
import FormFieldRenderer, { ScoreBar } from './FormFieldRenderer'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import type { CustomerReg, CustomerMeta } from '../../../../_types'

export default function RegisterForm({ editMode = false, initialData }: {
  editMode?: boolean
  initialData?: Partial<CustomerReg>
}) {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [loading,        setLoading]        = React.useState(true)
  const [form,           setForm]           = React.useState<FormDoc | null>(null)
  const [formData,       setFormData]       = React.useState<Record<string, any>>({})
  const [scores,         setScores]         = React.useState<Record<string, number>>({})
  const [pageErrors,     setPageErrors]     = React.useState<Record<number, string[]>>({})
  const [saving,         setSaving]         = React.useState(false)
  const [confirmShow,    setConfirmShow]    = React.useState(false)
  const [photos,         setPhotos]         = React.useState<{ file: File; preview: string; name: string; size: number }[]>([])
  const [photoUploading, setPhotoUploading] = React.useState(false)

  const pageRefs = React.useRef<Record<number, HTMLDivElement | null>>({})

  // ─── Load form config ─────────────────────────────────────────
  React.useEffect(() => {
    ;(async () => {
      try {
        const formRaw = await dbRead<any>(PATH.regForm)
        const fd: FormDoc = formRaw?.pages?.length
          ? { id: 'main', title: formRaw.title || 'ฟอร์มลงทะเบียน', pages: formRaw.pages }
          : { id: 'main', title: 'ฟอร์มลงทะเบียน', pages: [] }
        setForm(fd)
        const initVals = buildInitialValues(fd)
        if (editMode && initialData) {
          const flat: Record<string, any> = {}
          const flatten = (obj: any, prefix = '') => {
            for (const k in obj) {
              if (typeof obj[k] === 'object' && !Array.isArray(obj[k]) && obj[k] !== null)
                flatten(obj[k], k + '_')
              else flat[prefix + k] = obj[k]
            }
          }
          flatten(initialData)
          setFormData({ ...initVals, ...flat })
        } else {
          setFormData(initVals)
        }
      } catch (e: any) {
        toastDanger(e?.message || 'โหลดฟอร์มไม่สำเร็จ')
      } finally {
        setLoading(false)
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Handlers ────────────────────────────────────────────────
  const handleChange = (f: Field, val: any) =>
    setFormData((prev) => ({ ...prev, [f.name]: val }))

  const handleToggleMulti = (f: Field, v: string) => {
    const curr: string[] = Array.isArray(formData[f.name]) ? formData[f.name] : []
    const next = curr.includes(v) ? curr.filter((x) => x !== v) : [...curr, v]
    setFormData((prev) => ({ ...prev, [f.name]: next }))
  }

  const resetAll = () => {
    setFormData(buildInitialValues(form))
    setPageErrors({})
    photos.forEach((p) => URL.revokeObjectURL(p.preview))
    setPhotos([])
  }

  const totalScore = Object.values(scores).reduce((s, v) => s + v, 0)

  // ─── Validate all ─────────────────────────────────────────────
  const validateAll = (): boolean => {
    if (!form) return false
    const allErrors: Record<number, string[]> = {}
    let hasError = false
    form.pages.forEach((_, i) => {
      const errs = validatePage(form, i, formData)
      if (errs.length) { allErrors[i] = errs; hasError = true }
    })
    setPageErrors(allErrors)
    if (hasError) {
      const firstErrPage = Number(Object.keys(allErrors)[0])
      pageRefs.current[firstErrPage]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    return !hasError
  }

  const handleSubmitClick = () => {
    if (!validateAll()) return
    setConfirmShow(true)
  }

  // ─── Image resize ─────────────────────────────────────────────
  const resizeImage = (file: File, maxSize = 800, quality = 0.55): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let { width, height } = img
        if (width > maxSize || height > maxSize) {
          if (width >= height) { height = Math.round(height * maxSize / width); width = maxSize }
          else { width = Math.round(width * maxSize / height); height = maxSize }
        }
        const canvas = document.createElement('canvas')
        canvas.width = width; canvas.height = height
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('resize failed')),
          'image/jpeg', quality
        )
      }
      img.onerror = reject
      img.src = url
    })

  const handlePhotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    const newPhotos = await Promise.all(
      files.map(async (file) => {
        const resized = await resizeImage(file)
        const preview = URL.createObjectURL(resized)
        return { file: new File([resized], file.name, { type: 'image/jpeg' }), preview, name: file.name, size: resized.size }
      })
    )
    setPhotos((prev) => [...prev, ...newPhotos])
    e.target.value = ''
  }

  const removePhoto = (idx: number) => {
    setPhotos((prev) => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx) })
  }

  // ─── Save ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!uid) { toastDanger('ไม่พบข้อมูลผู้ใช้'); return }
    setSaving(true)
    try {
      const customerId = genCustomerId(uid)
      const meta: CustomerMeta = {
        status: 'รอการตรวจสอบ',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        uid,
        role: 'data_entry',
        pageCount: form?.pages?.length || 0,
        photoCount: photos.length,
      }
      const reg: CustomerReg = {
        key: customerId, meta,
        personal: {
          fullName: formData.fullName || '', nationalId: formData.nationalId || '',
          birthDate: formData.birthDate || '', age: String(formData.age || ''),
          phone: formData.phone || '', address: formData.address || '',
          maritalStatus: formData.maritalStatus || '',
          facebookLink: formData.facebookLink || '', locationLink: formData.locationLink || '',
        },
        work: {
          company: formData.company || '', position: formData.position || '',
          businessType: formData.businessType || '', workingPeriod: formData.workingPeriod || '',
          workAddress: formData.workAddress || '', workLocation: formData.workLocation || '',
          currentAddress: formData.currentAddress || '', sameAsIdAddress: formData.sameAsIdAddress || '',
        },
        finance: {
          dailyIncome: Number(formData.dailyIncome || 0),
          otherIncomeDaily: Number(formData.otherIncomeDaily || 0),
          debtDaily: Number(formData.debtDaily || 0),
          creditCardDaily: Number(formData.creditCardDaily || 0),
        },
        loan: {
          wantedAmount: Number(formData.wantedAmount || 0),
          purpose: formData.purpose || '',
          zone: Array.isArray(formData.zone) ? formData.zone : [formData.zone || ''],
          installmentDays: Number(formData.installmentDays || 30),
        },
        guarantor: {
          fullName: formData.guarantor_fullName || '', phone: formData.guarantor_phone || '',
          facebook: formData.guarantor_facebook || '', homeAddress: formData.guarantor_homeAddress || '',
          homeLocation: formData.guarantor_homeLocation || '', shopAddress: formData.guarantor_shopAddress || '',
          shopLocation: formData.guarantor_shopLocation || '', business: formData.guarantor_business || '',
        },
      }

      await saveNewCustomer(uid, customerId, reg)

      if (photos.length > 0) {
        setPhotoUploading(true)
        await Promise.all(
          photos.map(async (p) => {
            const key = `photo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
            const reader = new FileReader()
            const b64 = await new Promise<string>((res, rej) => {
              reader.onload = () => res((reader.result as string).split(',')[1])
              reader.onerror = rej
              reader.readAsDataURL(p.file)
            })
            await dbSet(DB_PHOTO_ITEM(customerId, key), {
              id: key, image: b64, name: p.name, size: p.size, type: 'image/jpeg',
              sort: 0, createdAt: Date.now(), uploadedBy: uid,
            })
          })
        )
        setPhotoUploading(false)
      }

      toastSuccess('บันทึกข้อมูลเรียบร้อย')
      resetAll()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── Loading / empty ──────────────────────────────────────────
  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <Spinner animation='border' variant='warning' />
        <span className='ms-2 text-muted'>กำลังโหลดแบบฟอร์ม...</span>
      </div>
    )
  }

  if (!form?.pages?.length) {
    return (
      <div className='p-4'>
        <Alert variant='warning'>
          <i className='fa-solid fa-triangle-exclamation me-2' />
          ยังไม่มีแบบฟอร์ม กรุณาไปที่ <strong>ตั้งค่าแบบฟอร์ม</strong> เพื่อสร้างฟอร์มก่อน
        </Alert>
      </div>
    )
  }

  const pages = form.pages
  const hasAnyError = Object.keys(pageErrors).length > 0

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className='p-3' style={{ paddingBottom: 80 }}>
      <ConfirmAlert
        show={confirmShow}
        title='ยืนยันการบันทึกข้อมูล'
        message='ตรวจสอบข้อมูลของท่านครบถูกต้องแล้ว กดยืนยันเพื่อบันทึก'
        confirmLabel={saving ? 'กำลังบันทึก...' : 'ยืนยัน'}
        variant='success'
        onConfirm={handleSave}
        onCancel={() => setConfirmShow(false)}
      />

      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>{form.title || 'ฟอร์มลงทะเบียน'}</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            {pages.length} ส่วน · กรอกแล้ว scroll ลงดูสรุปได้เลย
          </div>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <ScoreBar totalScore={totalScore} />
          {totalScore > 0 && <Badge bg='success'>{totalScore} คะแนน</Badge>}
        </div>
      </div>

      {/* Quick nav */}
      <div className='d-flex gap-1 flex-wrap mb-3'>
        {pages.map((pg, i) => (
          <Button key={i} size='sm'
            variant={pageErrors[i] ? 'danger' : 'outline-secondary'}
            style={{ fontSize: 11, padding: '2px 10px' }}
            onClick={() => pageRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {pg.title || `ส่วนที่ ${i + 1}`}
            {pageErrors[i] && <i className='fa-solid fa-circle-exclamation ms-1' />}
          </Button>
        ))}
        <Button size='sm' variant='outline-secondary' style={{ fontSize: 11, padding: '2px 10px' }}
          onClick={() => document.getElementById('form-photo-section')?.scrollIntoView({ behavior: 'smooth' })}>
          <i className='fa-solid fa-images me-1' />รูปภาพ
        </Button>
        <Button size='sm' variant='outline-secondary' style={{ fontSize: 11, padding: '2px 10px' }}
          onClick={() => document.getElementById('form-summary-section')?.scrollIntoView({ behavior: 'smooth' })}>
          <i className='fa-solid fa-list-check me-1' />สรุป
        </Button>
      </div>

      {/* Global error */}
      {hasAnyError && (
        <Alert variant='danger' style={{ fontSize: 13 }} onClose={() => setPageErrors({})} dismissible>
          <strong>กรุณาแก้ไขก่อนบันทึก:</strong>
          {Object.entries(pageErrors).map(([pi, errs]) => (
            <div key={pi}>
              <strong>{pages[Number(pi)]?.title || `ส่วนที่ ${Number(pi) + 1}`}:</strong>
              {errs.map((e, i) => <span key={i}> {e};</span>)}
            </div>
          ))}
        </Alert>
      )}

      {/* ── Page Cards ── */}
      {pages.map((pg, i) => (
        <div key={i} ref={(el) => { pageRefs.current[i] = el }} style={{ scrollMarginTop: 16 }}>
          <Card className='shadow-sm mb-3'
            style={{ borderColor: pageErrors[i] ? '#f44336' : undefined, borderWidth: pageErrors[i] ? 2 : 1 }}>
            <Card.Header style={{
              fontSize: 13, fontWeight: 700,
              background: pageErrors[i] ? '#fff5f5' : '#fafafa',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>
                <span style={{
                  background: pageErrors[i] ? '#f44336' : '#ffc107',
                  color: pageErrors[i] ? '#fff' : '#000',
                  borderRadius: 999, fontSize: 11, padding: '2px 9px', marginRight: 8,
                }}>{i + 1}</span>
                {pg.title || `ส่วนที่ ${i + 1}`}
              </span>
              {pageErrors[i] && (
                <span style={{ fontSize: 11, color: '#f44336' }}>
                  <i className='fa-solid fa-circle-exclamation me-1' />
                  {pageErrors[i].length} ช่องที่ต้องกรอก
                </span>
              )}
            </Card.Header>
            <Card.Body>
              <Row className='g-3'>
                {(pg.fields || []).map((f) => (
                  <FormFieldRenderer
                    key={f.name} field={f}
                    value={formData[f.name]}
                    onChange={handleChange}
                    onToggleMulti={handleToggleMulti}
                    showScore
                    scoreValue={scores[f.name] ?? 0}
                    onScoreChange={(field, val) => setScores((prev) => ({ ...prev, [field.name]: val }))}
                  />
                ))}
              </Row>
            </Card.Body>
          </Card>
        </div>
      ))}

      {/* ── Photo ── */}
      <div id='form-photo-section' style={{ scrollMarginTop: 16 }}>
        <Card className='shadow-sm mb-3'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
            <i className='fa-solid fa-images me-2' />รูปภาพประกอบ
            <span className='text-muted ms-2' style={{ fontSize: 11, fontWeight: 400 }}>
              ย่อสูงสุด 800px บีบอัดอัตโนมัติ
            </span>
          </Card.Header>
          <Card.Body>
            <div className='mb-3'>
              <Form.Label htmlFor='photo-upload' className='btn btn-sm btn-outline-primary' style={{ cursor: 'pointer' }}>
                <i className='fa-solid fa-plus me-1' />เพิ่มรูปภาพ
              </Form.Label>
              <Form.Control id='photo-upload' type='file' accept='image/*' multiple
                style={{ display: 'none' }} onChange={handlePhotoAdd} />
              {photos.length > 0 && (
                <span className='ms-2 text-muted' style={{ fontSize: 12 }}>
                  {photos.length} รูป · {(photos.reduce((s, p) => s + p.size, 0) / 1024).toFixed(0)} KB
                </span>
              )}
            </div>
            {photos.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                    <img src={p.preview} alt={p.name} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                    <button onClick={() => removePhoto(i)}
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.55)',
                        color: '#fff', border: 'none', borderRadius: 999, width: 22, height: 22, fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>✕
                    </button>
                    <div style={{ fontSize: 9, padding: '2px 4px', background: '#f5f5f5', color: '#888',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name} · {(p.size / 1024).toFixed(0)}KB
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-muted' style={{ fontSize: 12 }}>ยังไม่มีรูปภาพ</div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* ── Summary ── */}
      <div id='form-summary-section' style={{ scrollMarginTop: 16 }}>
        {totalScore > 0 && (
          <Card className='mb-3 shadow-sm' style={{ borderColor: '#ffc107' }}>
            <Card.Header style={{ fontSize: 13, fontWeight: 700, backgroundColor: '#fffbf0' }}>คะแนนพิจารณารวม</Card.Header>
            <Card.Body>
              <div className='d-flex align-items-center gap-3 flex-wrap'>
                {Object.entries(scores).map(([fieldName, score]) => {
                  if (!score) return null
                  const field = pages.flatMap((p) => p.fields).find((f) => f.name === fieldName)
                  if (!field) return null
                  return (
                    <div key={fieldName} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', fontSize: 12 }}>
                      <div style={{ color: '#888' }}>{field.label}</div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{score}</div>
                    </div>
                  )
                })}
                <div style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 15 }}>
                  รวม: <Badge bg='success' style={{ fontSize: 14 }}>{totalScore} คะแนน</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        <Card className='mb-3 shadow-sm'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
            <i className='fa-solid fa-list-check me-2' />สรุปข้อมูลทั้งหมด
          </Card.Header>
          <Card.Body style={{ maxHeight: 360, overflowY: 'auto', padding: 0 }}>
            <Table size='sm' bordered hover style={{ fontSize: 12, marginBottom: 0 }}>
              <tbody>
                {pages.flatMap((pg) =>
                  pg.fields.map((f) => (
                    <tr key={f.name}>
                      <td style={{ width: '40%', fontWeight: 600, color: '#555', padding: '5px 10px' }}>{f.label}</td>
                      <td style={{ padding: '5px 10px' }}>
                        {Array.isArray(formData[f.name])
                          ? formData[f.name].join(', ')
                          : formData[f.name] || <span className='text-muted'>-</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>

      {/* ── Sticky action bar ── */}
      <div style={{
        position: 'sticky', bottom: 0, zIndex: 50,
        background: '#fff', borderTop: '1px solid #e0e0e0',
        padding: '10px 0', display: 'flex', gap: 8,
      }}>
        <Button variant='warning' size='sm' onClick={resetAll}>
          <i className='fa-solid fa-rotate-left me-1' />ล้างทั้งหมด
        </Button>
        <Button variant='success' size='sm' className='ms-auto'
          onClick={handleSubmitClick} disabled={saving || photoUploading}>
          <i className='fa-solid fa-floppy-disk me-1' />
          {photoUploading ? 'กำลังอัปโหลดรูป...' : saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
        </Button>
      </div>
    </div>
  )
}